import { FC, useContext, useRef, useState } from 'react';
import { Button, makeStyles, Menu, MenuItem, Snackbar, Theme } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import { CurrentUserContext } from 'contexts/CurrentUserContext';
import { useTabs } from 'contexts/TabContext';
import TabButton from './TabButton';
import useTabOverflow from './useTabOverflow';

export const TAB_BAR_HEIGHT = 38;

/**
 * Sticky offset that lands the bar exactly under the fixed AppBar.
 *
 * <main> starts at the top of the viewport, behind the header, so `top: 0` would park the bar out of
 * sight underneath it. Derived from theme.mixins.toolbar — the same source the header spacer uses —
 * so the two stay in step across breakpoints and orientations instead of drifting apart.
 */
const stickyBelowAppBar = (toolbar: Record<string, any>) => {
  const offsets: Record<string, any> = { top: toolbar.minHeight };

  Object.keys(toolbar)
    .filter(key => key.startsWith('@media'))
    .forEach(key => {
      offsets[key] = { top: toolbar[key].minHeight };
    });

  return offsets;
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: TAB_BAR_HEIGHT,
    flex: 'none',
    display: 'flex',
    alignItems: 'stretch',
    paddingLeft: theme.spacing(0.75),
    /**
     * The bar shares the page's white surface rather than sitting on its own grey strip, so the
     * screen looks the same whether the tab bar is switched on or off. The active tab is told apart
     * by its blue underline and heavier label, which is what the spec asked for anyway.
     */
    backgroundColor: theme.palette.common.white,
    marginRight: 16,
    borderBottom: `1px solid ${theme.border.primary}`,
    /**
     * Sticky rather than a sibling of <main>: the bar stays put while the page scrolls (TAB-01)
     * without moving the scroll container everything else already depends on.
     */
    position: 'sticky',
    ...stickyBelowAppBar(theme.mixins.toolbar as Record<string, any>),
    zIndex: theme.zIndex.appBar - 1,
    overflow: 'hidden'
  },
  moreButton: {
    flex: 'none',
    alignSelf: 'center',
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.75),
    textTransform: 'none',
    fontSize: 12,
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary
  },
  menuItem: {
    fontSize: 13,
    minWidth: 200
  }
}));

/**
 * The in-app tab bar (TAB-01). Renders only where it belongs: for a signed in user, on a section
 * that owns tabs, and only while the feature is switched on.
 */
const TabBar: FC = () => {
  const classes = useStyles();
  const { currentUser } = useContext(CurrentUserContext);
  const { isTabNavActive, tabs, activeKey, flashKey, activateTab, closeTab, refreshTab, notice, dismissNotice } = useTabs();

  const containerRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef<HTMLDivElement>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const pinnedTab = tabs.find(tab => tab.pinned);
  const records = tabs.filter(tab => !tab.pinned);
  const { visible, hidden } = useTabOverflow(containerRef, pinnedRef, records, activeKey);

  if (!isTabNavActive || !currentUser || tabs.length === 0) {
    return null;
  }

  const handleMenuSelect = (key: string) => {
    setMenuAnchor(null);
    activateTab(key);
  };

  return (
    <>
      <div className={classes.root} ref={containerRef} role='tablist' aria-label='Open records'>
        {pinnedTab && (
          <TabButton
            ref={pinnedRef}
            tab={pinnedTab}
            isActive={pinnedTab.key === activeKey}
            isFlashing={pinnedTab.key === flashKey}
            onActivate={activateTab}
            onClose={closeTab}
            onRefresh={refreshTab}
          />
        )}
        {visible.map(tab => (
          <TabButton
            key={tab.key}
            tab={tab}
            isActive={tab.key === activeKey}
            isFlashing={tab.key === flashKey}
            onActivate={activateTab}
            onClose={closeTab}
            onRefresh={refreshTab}
          />
        ))}
        {hidden.length > 0 && (
          <Button
            size='small'
            className={classes.moreButton}
            endIcon={<ArrowDropDownIcon />}
            aria-label={`${hidden.length} more open tabs`}
            onClick={event => setMenuAnchor(event.currentTarget)}
          >
            {hidden.length} more
          </Button>
        )}
      </div>

      <Menu anchorEl={menuAnchor} keepMounted={false} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        {hidden.map(tab => (
          <MenuItem key={tab.key} className={classes.menuItem} onClick={() => handleMenuSelect(tab.key)}>
            {tab.label}
          </MenuItem>
        ))}
      </Menu>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={!!notice}
        message={notice}
        autoHideDuration={4000}
        onClose={dismissNotice}
      />
    </>
  );
};

export default TabBar;
